import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { SCHOOL_NAV } from "./SchoolDashboard";
import api from "@/lib/api";
import { toast } from "sonner";

export default function RFQs() {
  const [rfqs, setRfqs] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [f, setF] = useState({ requisition_id: "", title: "", category: "Textbooks", itemName: "", qty: 1, vendorIds: [] });

  const load = useCallback(async () => {
    const [r, req, v] = await Promise.all([api.get("/rfqs"), api.get("/requisitions"), api.get("/vendors")]);
    setRfqs(r.data); setRequisitions(req.data.filter((x) => x.status === "approved")); setVendors(v.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openRfq = async (rfq) => {
    setSelected(rfq);
    const { data } = await api.get(`/rfqs/${rfq.id}/quotations`);
    setQuotes(data);
  };

  const create = async (e) => {
    e.preventDefault();
    if (f.vendorIds.length === 0) return toast.error("Select at least one vendor");
    try {
      await api.post("/rfqs", {
        requisition_id: f.requisition_id || null,
        title: f.title,
        category: f.category,
        items: [{ name: f.itemName, qty: Number(f.qty) }],
        vendor_ids: f.vendorIds,
      });
      toast.success("RFQ sent to vendors");
      setShowForm(false);
      setF({ requisition_id: "", title: "", category: "Textbooks", itemName: "", qty: 1, vendorIds: [] });
      load();
    } catch { toast.error("Failed to create RFQ"); }
  };

  const award = async (rfqId, quoteId) => {
    try {
      await api.post("/purchase-orders", { rfq_id: rfqId, quotation_id: quoteId });
      toast.success("Purchase Order generated");
      setSelected(null);
      load();
    } catch { toast.error("Failed"); }
  };

  const toggleVendor = (id) => setF({ ...f, vendorIds: f.vendorIds.includes(id) ? f.vendorIds.filter((x) => x !== id) : [...f.vendorIds, id] });

  return (
    <DashboardLayout nav={SCHOOL_NAV} title="Request for Quotations">
      <div className="flex justify-between items-center mb-4">
        <p className="text-slate-600 text-sm">Send RFQs to vendors and compare quotations side-by-side.</p>
        <button data-testid="new-rfq" onClick={() => setShowForm(!showForm)} className="rounded-md bg-brand-blue text-white font-semibold px-4 py-2 hover:bg-brand-blueDark transition-colors duration-200">
          {showForm ? "Cancel" : "Create RFQ"}
        </button>
      </div>
      {showForm && (
        <form onSubmit={create} className="sl-card p-5 mb-6 grid md:grid-cols-2 gap-3">
          <select value={f.requisition_id} onChange={(e) => setF({...f, requisition_id: e.target.value})} className="rounded-md border border-slate-300 px-3 py-2" data-testid="rfq-req">
            <option value="">— Link approved requisition (optional) —</option>
            {requisitions.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
          </select>
          <input required placeholder="RFQ title" value={f.title} onChange={(e) => setF({...f, title: e.target.value})} data-testid="rfq-title" className="rounded-md border border-slate-300 px-3 py-2" />
          <select value={f.category} onChange={(e) => setF({...f, category: e.target.value})} data-testid="rfq-category" className="rounded-md border border-slate-300 px-3 py-2">
            {["Textbooks","Library Books","Stationery","Uniforms","Shoes","Laboratory Equipment","Sports Equipment","Furniture","IT Equipment","STEM Kits"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input required placeholder="Item name" value={f.itemName} onChange={(e) => setF({...f, itemName: e.target.value})} data-testid="rfq-item" className="rounded-md border border-slate-300 px-3 py-2" />
          <input type="number" min="1" value={f.qty} onChange={(e) => setF({...f, qty: e.target.value})} data-testid="rfq-qty" className="rounded-md border border-slate-300 px-3 py-2" />
          <div className="md:col-span-2">
            <div className="text-xs font-semibold text-slate-700 mb-2">Select vendors ({f.vendorIds.length})</div>
            <div className="grid md:grid-cols-2 gap-2">
              {vendors.map((v) => (
                <label key={v.id} className={`flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer ${f.vendorIds.includes(v.id) ? "border-brand-blue bg-blue-50" : "border-slate-200"}`}>
                  <input type="checkbox" checked={f.vendorIds.includes(v.id)} onChange={() => toggleVendor(v.id)} data-testid={`vendor-check-${v.id}`} />
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold font-mono">{v.supplier_ref || v.id.slice(0, 8)}</span>
                    <span className={`text-[10px] font-semibold ${v.supplier_tier === "Preferred Supplier" ? "text-brand-greenDark" : "text-brand-blue"}`}>{v.supplier_tier || "Approved Supplier"}</span>
                  </div>
                </label>
              ))}
              {vendors.length === 0 && <div className="text-slate-500 text-sm">No suppliers approved yet.</div>}
            </div>
          </div>
          <button data-testid="rfq-submit" className="md:col-span-2 rounded-md bg-brand-blue text-white font-semibold py-2 hover:bg-brand-blueDark transition-colors duration-200">Send RFQ</button>
        </form>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {rfqs.map((r) => (
          <button key={r.id} onClick={() => openRfq(r)} data-testid={`rfq-${r.id}`} className="sl-card p-5 text-left">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading font-semibold text-slate-900">{r.title}</div>
                <div className="text-xs text-slate-500 mt-1">{(r.supplier_refs || r.vendor_ids || []).length} vendors · {new Date(r.created_at).toLocaleDateString('en-IN')}</div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${r.status === "open" ? "bg-blue-50 text-brand-blue" : "bg-emerald-50 text-brand-greenDark"}`}>{r.status}</span>
            </div>
          </button>
        ))}
        {rfqs.length === 0 && <div className="sl-card p-8 text-slate-500 md:col-span-2 text-center">No RFQs yet.</div>}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl border border-slate-200 max-w-3xl w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-bold text-xl">{selected.title} — Quotation Comparison</h3>
            <p className="text-sm text-slate-500 mt-1">Compare vendor prices and award the PO.</p>
            <div className="mt-4 divide-y divide-slate-100">
              {quotes.length === 0 && <div className="text-slate-500 text-sm py-6">No quotations submitted yet.</div>}
              {quotes.sort((a, b) => a.total - b.total).map((q, i) => (
                <div key={q.id} className="py-4 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-900 flex items-center gap-2 font-mono text-sm">{q.supplier_ref} {i === 0 && <span className="text-xs bg-emerald-50 text-brand-greenDark px-2 py-0.5 rounded-full">Best price</span>}</div>
                    <div className={`text-xs font-semibold ${q.supplier_tier === "Preferred Supplier" ? "text-brand-greenDark" : "text-brand-blue"}`}>{q.supplier_tier}</div>
                    <div className="text-xs text-slate-500">Delivery in {q.delivery_days || 7} days</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="font-bold text-lg">₹{Number(q.total).toLocaleString('en-IN')}</div>
                    {selected.status === "open" && <button onClick={() => award(selected.id, q.id)} data-testid={`award-${q.id}`} className="rounded-md bg-brand-green text-white text-sm px-3 py-2 hover:bg-brand-greenDark transition-colors duration-200">Award & Generate PO</button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
